import { Signal, useSignal } from '@preact/signals';

export interface IUseFetchOptions<T> {
  method?: string;
  headers?: HeadersInit;
  body?: BodyInit | null;
}

interface IUseFetchResult<I, O> {
  data: Signal<O | null>;
  isLoading: Signal<boolean>;
  error: Signal<string | null>;
  success: Signal<boolean>;
  handleFetch: (data: I) => void;
}

export const useFetch = <I, O>(
  url: string,
  options: IUseFetchOptions<I> = {},
): IUseFetchResult<I, O> => {
  const data = useSignal<O | null>(null);
  const isLoading = useSignal<boolean>(false);
  const error = useSignal<string | null>(null);
  const success = useSignal<boolean>(false);

  const fetchData = async (fetchData: I) => {
    isLoading.value = true;
    try {
      const requestOptions: IUseFetchOptions<I> = { ...options };

      if (fetchData) {
        if (typeof fetchData === 'object' && !(fetchData instanceof FormData)) {
          requestOptions.body = JSON.stringify(fetchData);
          requestOptions.headers = {
            ...requestOptions.headers,
            'Content-Type': 'application/json; charset=utf-8',
          };
        } else {
          requestOptions.body = fetchData as any;
        }
      }

      const response = await fetch(url, requestOptions);

      if (!response.ok) throw new Error(response.statusText);

      success.value = response.ok;

      const json = (await response.json()) as O;

      isLoading.value = false;
      data.value = json;
      error.value = null;
    } catch (err) {
      error.value = `${error} Error fetching`;
      isLoading.value = false;
      success.value = false;
    }
  };

  return {
    data: data,
    isLoading: isLoading,
    error: error,
    success: success,
    handleFetch: fetchData,
  };
};
